import React from 'react';
import { DragOverlay } from '@dnd-kit/core';
import { SlotInfo, SlotType } from '../../../../types/schedule.types';
import { SceneInfo } from '../../../../types/scene.types';
import { NoteCardPreview, StandaloneSoltCardPreview, SceneCardPreview } from '../Calendar/DayInfo/CardPreviews';
import { buildDisplaySegments } from '../../../../utils/calDateSlotsSortUtils';
import type { ClipboardScheduleItem } from '../../Hooks/useScheduleClipboard';

export interface SlotDragOverlayProps {
  shouldHideDragOverlay: boolean;
  activeDragType:
    | 'slot'
    | 'scene'
    | 'wholeDay'
    | 'clipboard'
    | 'clipboardGroup'
    | null;
  activeDragData: any;
  sceneDragOverlayWidth: number | null;
  wholeDayOverlayWidth: number | null;
  slotOverlayWidth: number | null;
  activeSortingType: 'slot' | null;
  activeSortingId: string | null;
  activeSortingDate: string | null;
  activeSlotDragBundleIds: string[] | null;
  optimisticSlots: SlotInfo[];
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
  getCalendarRowIdsForDateStr: (dateStr: string) => string[];
  /** “显示所有地点”模式：UI 忽略 slotLocationVisibility */
  forceShowAllLocations?: boolean;
}

/** 日历 DnD 的 DragOverlay：日程 slot 卡片为主（含整日 bundle / scene / clipboard），DOM/class 与历史保持一致 */
export const SlotDragOverlay: React.FC<SlotDragOverlayProps> = ({
  shouldHideDragOverlay,
  activeDragType,
  activeDragData,
  sceneDragOverlayWidth,
  wholeDayOverlayWidth,
  slotOverlayWidth,
  activeSortingType,
  activeSortingId,
  activeSortingDate,
  activeSlotDragBundleIds,
  optimisticSlots,
  getSceneInfo,
  getCalendarRowIdsForDateStr,
  forceShowAllLocations = false,
}) => (
  <DragOverlay
    dropAnimation={null}
    style={
      activeDragType === 'scene'
        ? {
            height: 'auto',
            overflow: 'visible',
            ...(sceneDragOverlayWidth != null
              ? { width: sceneDragOverlayWidth, minWidth: 0, boxSizing: 'border-box' as const }
              : {}),
          }
        : activeDragType === 'wholeDay'
          ? { width: 200, minWidth: 200, height: 'auto', minHeight: 100, overflow: 'visible' }
          : activeDragType === 'clipboard' || activeDragType === 'clipboardGroup'
            ? { width: 200, minWidth: 200, height: 'auto', overflow: 'visible' }
            : activeDragType === 'slot'
              ? { height: 'auto', maxHeight: 'none', overflow: 'visible' }
              : undefined
    }
  >
    {!shouldHideDragOverlay && activeDragType === 'wholeDay' && activeDragData?.slots?.length > 0 && (() => {
      const slots = activeDragData.slots as SlotInfo[];
      const sourceDateStr = activeDragData.sourceDateStr as string;
      const segments = buildDisplaySegments(
        slots,
        activeDragData.calDateSlotsSort,
        sourceDateStr,
        (sceneID) => {
          const s = getSceneInfo(sceneID);
          return s ? { sceneLocation: s.sceneLocation } : undefined;
        },
        { forceShowAllLocations: forceShowAllLocations },
      );
      const slotMap = new Map(slots.map((t) => [t.slotID, t]));
      const orderedIds = segments.flatMap((s) => s.items);
      let firstSoltIdInSourceDay: string | null = null;
      for (const id of orderedIds) {
        const row = slotMap.get(id);
        if (row && (row.slotType === SlotType.SHOOT || !row.slotType)) {
          firstSoltIdInSourceDay = id;
          break;
        }
      }
      return (
        <div
          className="dayInfo-slot-cards-drag-overlay-container"
          style={wholeDayOverlayWidth != null ? { width: wholeDayOverlayWidth } : { minWidth: 190 }}
        >
          <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
            {orderedIds.map((id, i) => {
              const t = slotMap.get(id);
              if (!t) return null;
              return (
                <React.Fragment key={id}>
                  {i > 0 ? <div className="dayInfo-slot-insert-gap" aria-hidden /> : null}
                  <div className="dayInfo-slot-cards-drag-overlay-item">
                    {t.slotType === SlotType.NOTE ? (
                      <NoteCardPreview content={t.slotName || ''} />
                    ) : (
                      <StandaloneSoltCardPreview
                        slot={t}
                        scene={getSceneInfo(t.slotBelongSceneID)}
                        isFirstSoltCardInDay={firstSoltIdInSourceDay === id}
                        forceShowAllLocations={forceShowAllLocations}
                      />
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      );
    })()}
    {!shouldHideDragOverlay && activeDragType === 'scene' && activeDragData?.scene && (
      <div
        className="dayInfo-slot-drag-preview"
        style={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}
      >
        <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
          <div className="dayInfo-slot-cards-drag-overlay-item">
            <SceneCardPreview scene={activeDragData.scene} />
          </div>
        </div>
      </div>
    )}
    {!shouldHideDragOverlay && activeDragType === 'clipboardGroup' &&
      Array.isArray(activeDragData?.clipboardGroupItems) &&
      activeDragData.clipboardGroupItems.length > 0 &&
      (() => {
        const groupItems = activeDragData.clipboardGroupItems as ClipboardScheduleItem[];
        return (
          <div
            className="dayInfo-slot-drag-preview"
            style={{ borderRadius: 2, overflow: 'hidden', maxHeight: 320 }}
          >
            <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
              {groupItems.map((item, gi) => (
                <React.Fragment key={`cg-seg-${gi}`}>
                  {gi > 0 ? <div className="dayInfo-slot-insert-gap" aria-hidden /> : null}
                  <div className="dayInfo-slot-cards-drag-overlay-item">
                    {item.type === 'NOTE' ? (
                      <NoteCardPreview content={item.slots[0]?.slotName || ''} />
                    ) : (
                      <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
                        {item.slots.map((pt, i) => (
                          <React.Fragment key={pt.slotID}>
                            {i > 0 ? <div className="dayInfo-slot-insert-gap" aria-hidden /> : null}
                            <div className="dayInfo-slot-cards-drag-overlay-item">
                              <StandaloneSoltCardPreview
                                slot={pt}
                                scene={getSceneInfo(pt.slotBelongSceneID)}
                                forceShowAllLocations={forceShowAllLocations}
                              />
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })()}
    {!shouldHideDragOverlay && activeDragType === 'clipboard' && activeDragData?.clipboardItem && (() => {
      const item = activeDragData.clipboardItem as ClipboardScheduleItem;
      if (item.type === 'NOTE') {
        return (
          <div className="dayInfo-slot-drag-preview" style={{ borderRadius: 2, overflow: 'hidden' }}>
            <NoteCardPreview content={item.slots[0]?.slotName || ''} />
          </div>
        );
      }
      return (
        <div className="dayInfo-slot-drag-preview" style={{ borderRadius: 2, overflow: 'hidden' }}>
          <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
            {item.slots.map((pt, i) => (
              <React.Fragment key={pt.slotID}>
                {i > 0 ? <div className="dayInfo-slot-insert-gap" aria-hidden /> : null}
                <div className="dayInfo-slot-cards-drag-overlay-item">
                  <StandaloneSoltCardPreview
                    slot={pt}
                    scene={getSceneInfo(pt.slotBelongSceneID)}
                    forceShowAllLocations={forceShowAllLocations}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      );
    })()}
    {activeSortingType === 'slot' && activeSortingId && activeSortingDate && activeDragData && (() => {
      const dragDataSlot = activeDragData.slot as SlotInfo | undefined;
      const noteSlotID = activeSortingId.includes('__note-')
        ? activeSortingId.split('__note-')[1]
        : activeSortingId.startsWith('note-')
          ? activeSortingId.replace('note-', '')
          : null;
      const resolvedSlot = dragDataSlot || (noteSlotID
        ? optimisticSlots.find((t) => t.slotID === noteSlotID)
        : optimisticSlots.find((t) => t.slotID === activeSortingId));
      if (!resolvedSlot) return null;
      const scene = getSceneInfo(resolvedSlot.slotBelongSceneID);
      const isNote = resolvedSlot.slotType === SlotType.NOTE;

      let firstSoltIdInSourceDay: string | null = null;
      const sourceRowIds = getCalendarRowIdsForDateStr(activeSortingDate);
      for (const id of sourceRowIds) {
        const rowSolt = optimisticSlots.find((p) => p.slotID === id);
        if (rowSolt && (rowSolt.slotType === SlotType.SHOOT || !rowSolt.slotType)) {
          firstSoltIdInSourceDay = id;
          break;
        }
      }

      let inner: React.ReactElement;
      if (isNote || activeDragData.dragLevel === 'note') {
        inner = (
          <div className="dayInfo-slot-drag-preview" style={{ borderRadius: 2, overflow: 'hidden' }}>
            <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
              <div className="dayInfo-slot-cards-drag-overlay-item">
                <NoteCardPreview content={resolvedSlot.slotName || ''} />
              </div>
            </div>
          </div>
        );
      } else {
        const bundleForOverlay =
          activeDragData.dragLevel === 'slot' &&
          activeSlotDragBundleIds &&
          activeSlotDragBundleIds.length > 1
            ? activeSlotDragBundleIds
            : null;
        if (bundleForOverlay) {
          const bundleSlots = bundleForOverlay
            .map((tid) => optimisticSlots.find((t) => t.slotID === tid))
            .filter((t): t is SlotInfo => t != null);
          if (bundleSlots.length === 0) return null;
          inner = (
            <div
              className="dayInfo-slot-drag-preview dayInfo-slot-drag-preview--slot-bundle-stack"
              style={{ borderRadius: 2 }}
            >
              <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
                {bundleSlots.map((pt, i) => (
                  <React.Fragment key={pt.slotID}>
                    {i > 0 ? <div className="dayInfo-slot-insert-gap" aria-hidden /> : null}
                    <div className="dayInfo-slot-cards-drag-overlay-item">
                      <StandaloneSoltCardPreview
                        slot={pt}
                        scene={getSceneInfo(pt.slotBelongSceneID)}
                        isFirstSoltCardInDay={firstSoltIdInSourceDay === pt.slotID}
                        forceShowAllLocations={forceShowAllLocations}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        } else {
          inner = (
            <div
              className="dayInfo-slot-drag-preview dayInfo-slot-drag-preview--slot-bundle-stack"
              style={{ borderRadius: 2 }}
            >
              <div className="dayInfo-slot-cards dayInfo-slot-cards-drag-overlay dayInfo-slot-cards-drag-stack">
                <div className="dayInfo-slot-cards-drag-overlay-item">
                  <StandaloneSoltCardPreview
                    slot={resolvedSlot}
                    scene={scene}
                    isFirstSoltCardInDay={firstSoltIdInSourceDay === resolvedSlot.slotID}
                    forceShowAllLocations={forceShowAllLocations}
                  />
                </div>
              </div>
            </div>
          );
        }
      }

      if (slotOverlayWidth != null) {
        return (
          <div
            style={{
              width: slotOverlayWidth,
              minWidth: 0,
              boxSizing: 'border-box',
              overflow: 'visible',
              height: 'auto',
            }}
          >
            {inner}
          </div>
        );
      }
      return inner;
    })()}
  </DragOverlay>
);
